import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { CaretUpDown, CheckCircle, X } from "@phosphor-icons/react";
import Ship from "@/components/icons/ship";
import { useMemo, useState } from "react";
import { cn, getFlagByName } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useGetVessels } from "@/services/queries/vessel";
import { Vessel } from "@/lib/types";
import useVessel from "@/store/use-vessel";
import { Mixpanel } from "@/lib/mixpanel";

const SelectingBoatButton = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { selectedVessel, setSelectedVessel } = useVessel();
  const { data } = useGetVessels();

  const vessels = useMemo(() => {
    return data?.value || [];
  }, [data]);

  const handleSelectVessel = (vessel: Vessel) => {
    setSelectedVessel(vessel);
    setIsOpen(false);
    localStorage.setItem("selected_vessel", vessel.id);
    Mixpanel.track("Change Main Vessel");
  };

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerTrigger
        data-testid="select-vessel-button"
        className="flex flex-row items-center text-white"
      >
        <Ship size={22} />{" "}
        <span className="ml-3 mr-[7px] text-[15px] font-medium">
          {selectedVessel ? selectedVessel.name : t("label.boat_name")}
        </span>{" "}
        <CaretUpDown className="text-white/[60%]" weight="bold" size={12} />
      </DrawerTrigger>
      <DrawerContent data-testid="select-vessel-drawer" className="h-auto">
        <DrawerHeader className="hidden">
          <DrawerTitle>{t("title.select_boat")}</DrawerTitle>
          <DrawerDescription>{t("title.select_boat")}</DrawerDescription>
        </DrawerHeader>
        <div className="flex flex-row items-center justify-between">
          <span className="flex w-6"></span>
          <span className="text-[15px] font-semibold tracking-wide text-white">
            {t("title.select_boat").toLocaleUpperCase(i18n.language)}
          </span>
          <DrawerClose data-testid="select-vessel-drawer-close">
            <X color="#26C2DD" weight="bold" size={22} />
          </DrawerClose>
        </div>

        <div className="mt-5 space-y-3">
          {vessels.map((vessel: Vessel) => {
            const isSelected = vessel.id === selectedVessel?.id;
            return (
              <div
                role="button"
                data-testid={`vessel-item-${vessel.id}`}
                aria-selected={isSelected}
                onClick={() => handleSelectVessel(vessel)}
                className={cn(
                  "flex cursor-pointer flex-row items-center justify-between rounded-lg border border-[#DDDDDD]/20 px-4 py-3",
                  isSelected && "border-[#26C2DD] outline outline-[#26C2DD]"
                )}
                key={vessel.id}
              >
                <div className="flex flex-row items-center gap-[14px]">
                  <div className="text-[20px]">
                    {getFlagByName(vessel.flagState)}
                  </div>

                  <div className="flex flex-col gap-[3px] text-sm">
                    <span className="leading-[15px] text-white">
                      {vessel.name}
                    </span>
                    {vessel.notes && (
                      <span className="leading-[14px] text-white/50">
                        {vessel.notes}
                      </span>
                    )}
                  </div>
                </div>

                {isSelected && (
                  <CheckCircle color="#26C2DD" size={24} weight="fill" />
                )}
              </div>
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default SelectingBoatButton;
